import { z } from "zod";

import {
  DOC_STORAGE_SERVICES,
  type DocStorageService
} from "../../../../shared/domain/oauth/oauth-service.types";

/**
 * Miscellaneous document operations
 * Includes: Process, Move, VerifyUrl, FetchSitemap, VerifyWebsite, GetStatistics, TestQuery, GetProviderToken
 */

// ============================================================================
// PROCESS
// ============================================================================

export const DocumentProcessPathVarsSchema = z.object({
  id: z.string()
});

export const DocumentProcessResponseSchema = z.object({
  message: z.string()
});

// ============================================================================
// MOVE
// ============================================================================

export const DocumentMovePathVarsSchema = z.object({
  id: z.string()
});

export const DocumentMoveBodySchema = z.object({
  newParentId: z.string().nullable()
});

// ============================================================================
// GET STATISTICS
// ============================================================================

export const DocumentGetStatisticsResponseSchema = z.object({
  totalDocuments: z.number(),
  totalSize: z.number(),
  documentsByType: z.record(z.string(), z.number()),
  documentsByStatus: z.record(z.string(), z.number())
});

// ============================================================================
// TEST QUERY
// ============================================================================

export const DocumentTestQueryPathVarsSchema = z.object({
  id: z.string()
});

export const DocumentTestQueryBodySchema = z.object({
  query: z.string(),
  k: z.number().int().optional()
});

export const DocumentTestQueryResponseSchema = z.object({
  query: z.string(),
  documentId: z.string(),
  documentName: z.string(),
  results: z.array(
    z.object({
      content: z.string(),
      score: z.number(),
      metadata: z.record(z.string(), z.unknown()).optional()
    })
  ),
  total: z.number()
});

// ============================================================================
// DOWNLOAD URL
// ============================================================================

export const DocumentDownloadUrlPathVarsSchema = z.object({
  id: z.string()
});

export const DocumentDownloadUrlResponseSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  expiresIn: z.number()
});

// ============================================================================
// PREVIEW URL
// ============================================================================

export const DocumentPreviewUrlPathVarsSchema = z.object({
  id: z.string()
});

export const DocumentPreviewUrlResponseSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  expiresIn: z.number()
});

// ============================================================================
// GET SERVICE TOKEN
// ============================================================================

export const DocumentGetProviderTokenPathVarsSchema = z.object({
  service: z
    .string()
    .refine(
      (val): val is DocStorageService => DOC_STORAGE_SERVICES.includes(val as DocStorageService),
      {
        message: `Service must be one of: ${DOC_STORAGE_SERVICES.join(", ")}`
      }
    )
});

export const DocumentGetProviderTokenResponseSchema = z.union([
  z.object({
    status: z.literal("ready"),
    accessToken: z.string(),
    expiresAt: z.string(),
    metadata: z.record(z.string(), z.unknown())
  }),
  z.object({
    status: z.literal("not_connected")
  }),
  z.object({
    status: z.literal("needs_reauth"),
    reason: z.string()
  })
]);
